import React, { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { RISK_META, TYPE_LABEL } from "../lib/risk";
import { useI18n } from "../lib/i18n";

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedPage({ text, highlight }) {
  if (!highlight || highlight.length < 3) {
    return <p className="whitespace-pre-wrap leading-relaxed">{text}</p>;
  }
  const snippet = highlight.replace(/…$/, "").trim();
  const idx = text.toLowerCase().indexOf(snippet.toLowerCase());
  if (idx === -1) {
    return <p className="whitespace-pre-wrap leading-relaxed">{text}</p>;
  }
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + snippet.length);
  const after = text.slice(idx + snippet.length);
  return (
    <p className="whitespace-pre-wrap leading-relaxed">
      {before}
      <mark className="highlight-finding" data-testid="doc-highlight" id="doc-highlight">
        {match}
      </mark>
      {after}
    </p>
  );
}

export default function Viewer() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [selected, setSelected] = useState(parseInt(sp.get("f") || "0", 10));

  const { data, isLoading } = useQuery({
    queryKey: ["analysis", id],
    queryFn: async () => (await api.get(`/analyses/${id}`)).data,
  });

  const activeFinding = data?.findings?.[selected];
  const pages = data?.document?.pages || [];
  const activePage = useMemo(() => {
    if (!activeFinding) return pages[0];
    return pages.find((p) => p.page === activeFinding.page) || pages[0];
  }, [activeFinding, pages]);

  if (isLoading || !data)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        {t("common.loading")}
      </div>
    );

  return (
    <div className="h-screen flex flex-col">
      <header className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <button
          onClick={() => navigate(`/result/${id}`)}
          data-testid="viewer-back-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors"
        >
          <ArrowLeft size={18} weight="bold" />
          {t("viewer.back")}
        </button>
        <span className="text-slate-300">/</span>
        <span className="text-sm text-slate-500 truncate">{data.analysis.filename}</span>
      </header>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {/* Document */}
        <div className="md:w-[60%] h-1/2 md:h-full overflow-auto bg-[#F1F3F6] p-5 md:p-8">
          <div className="max-w-2xl mx-auto bg-white shadow-[0_2px_16px_rgba(10,17,40,0.08)] rounded-sm border border-slate-200 min-h-full p-8 md:p-12">
            <div className="label-caps mb-4">
              {t("viewer.page")} {activePage?.page ?? 1}
            </div>
            <div className="text-[13px] text-navy font-mono" data-testid="viewer-document">
              {activePage?.text ? (
                <HighlightedPage text={activePage.text} highlight={activeFinding?.text} />
              ) : (
                <p className="text-slate-400">—</p>
              )}
            </div>
          </div>
        </div>

        {/* Findings panel */}
        <div className="md:w-[40%] h-1/2 md:h-full overflow-auto border-t md:border-t-0 md:border-l border-slate-200 bg-white p-5">
          <h2 className="label-caps mb-3">
            {t("res.findings")} ({data.findings.length})
          </h2>
          <div className="space-y-2">
            {data.findings.map((f, i) => {
              const risk = RISK_META[f.risk] || RISK_META.MEDIUM;
              const isActive = i === selected;
              const label = (TYPE_LABEL[f.type] || { pt: f.type, en: f.type })[lang];
              return (
                <button
                  key={f.id || i}
                  onClick={() => {
                    setSelected(i);
                    setTimeout(
                      () => document.getElementById("doc-highlight")?.scrollIntoView({ behavior: "smooth", block: "center" }),
                      50
                    );
                  }}
                  data-testid={`viewer-finding-${i}`}
                  className={`w-full text-left rounded-md border p-4 transition-all duration-200 ${
                    isActive
                      ? "border-navy bg-navy/[0.03] shadow-[0_4px_12px_-6px_rgba(10,17,40,0.2)]"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: risk.color }} />
                      <span className="text-sm font-bold text-navy truncate">{label}</span>
                    </span>
                    <CaretRight size={14} className={isActive ? "text-navy" : "text-slate-300"} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {t("find.page")} {f.page} ·{" "}
                    <span style={{ color: risk.color }} className="font-semibold">
                      {lang === "pt" ? risk.label : risk.labelEn}
                    </span>
                  </div>
                  {isActive && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs font-mono text-navy bg-slate-50 rounded px-2 py-1.5 break-words">
                        “{f.text}”
                      </p>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">{f.reason}</p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
